import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaUserAlt } from "react-icons/fa";
import { useCreateUserMutation } from "../Admin/adminApiSlice";

const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    roles: "Creator",
  });

  const [createUser, { isLoading, isError, isSuccess, error }] =
    useCreateUserMutation();
  const navigate = useNavigate();

  // Load user details from localStorage on component mount
  useEffect(() => {
    const storedUserDetails = JSON.parse(
      localStorage.getItem("userCompanyDetails")
    );
    if (storedUserDetails) {
      setFormData((prev) => ({
        ...prev,
        name: storedUserDetails.name || "",
        email: storedUserDetails.email || "",
      }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      return; // Stop execution if passwords don't match
    }

    try {
      const { name, email, password, roles } = formData;

      // Call the createUser mutation with form data
      await createUser({
        username: email,
        password,
        email,
        name,
        roles,
      }).unwrap();

      // Navigate to login page if registration is successful
      setTimeout(() => {
        navigate(`/login/?user=${email}`);
      }, 1500); // Navigate after 1.5 seconds to give the success message time to show
    } catch (err) {
      console.error("Registration failed: ", err);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen py-4 bg-gray-100">
      <main className="w-full max-w-4xl flex flex-col lg:flex-row bg-white shadow-md rounded-lg overflow-hidden">
        <section className="flex flex-col justify-evenly items-center lg:w-1/2 p-4">
          <img src="/assets/logo.png" alt="Logo" className="w-1/2 mb-4" />
          <h2 className="text-2xl font-semibold text-blue-950 w-full text-center mb-2">
            Create a new account
          </h2>
          <form
            onSubmit={handleSubmit}
            className="w-full flex flex-col gap-4 items-center"
          >
            {/* Name Field */}
            <div className="w-full flex flex-col gap-4 relative">
              <label
                htmlFor="name"
                className="text-lg font-semibold w-full text-left"
              >
                Full Name:
              </label>
              <div className="relative">
                <FaUserAlt className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="text-lg rounded border border-gray-300 px-4 py-1 pl-10 w-full"
                  placeholder="Enter your full name"
                />
              </div>
            </div>

            {/* Email Field */}
            <div className="w-full flex flex-col gap-4 relative">
              <label
                htmlFor="email"
                className="text-lg font-semibold w-full text-left"
              >
                Email Address:
              </label>
              <div className="relative">
                <FaUserAlt className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="text-lg rounded border border-gray-300 px-4 py-1 pl-10 w-full"
                  placeholder="Enter your email address"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="w-full flex flex-col gap-4 relative">
              <label
                htmlFor="password"
                className="text-lg font-semibold w-full text-left"
              >
                Password:
              </label>
              <div className="relative">
                <FaUserAlt className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="text-lg rounded border border-gray-300 px-4 py-1 pl-10 w-full"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            {/* Confirm Password Field */}
            <div className="w-full flex flex-col gap-4 relative">
              <label
                htmlFor="confirmPassword"
                className="text-lg font-semibold w-full text-left"
              >
                Confirm Password:
              </label>
              <div className="relative">
                <FaUserAlt className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="text-lg rounded border border-gray-300 px-4 py-1 pl-10 w-full"
                  placeholder="Confirm your password"
                />
              </div>
            </div>

            <div className="flex items-center justify-between w-full mt-4">
              <button
                className="w-40 text-lg tracking-wide font-normal h-12 border bg-gray-200 rounded"
                type="button"
                onClick={() => navigate("/login")}
              >
                Cancel
              </button>
              <button
                className="w-40 rounded text-lg tracking-wide font-normal h-12 border bg-blue-950 text-white"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? "Registering..." : "Register"}
              </button>
            </div>
          </form>

          {/* Display error or success message */}
          {isError && (
            <div className="text-red-500 text-lg mt-4">
              {error?.data?.message || "Registration failed. Please try again."}
            </div>
          )}
          {isSuccess && (
            <div className="text-green-500 text-lg mt-4">
              Registration successful! Please log in.
            </div>
          )}
        </section>
        <div className="bg-gray-300 border relative top-0 lg:left-[0.7rem] md:h-[2px] w-[90%] md:w-[2px] lg:h-[75vh]"></div>
        <section className="lg:w-1/2 flex flex-col items-center justify-center bg-gray-200 p-4">
          <h2 className="text-2xl lg:text-3xl text-center mb-4">
            Data Visualized, Decisions Amplified
          </h2>
          <img
            src="/assets/Data-report.png"
            alt="dataReport"
            className="w-3/4 mb-4"
          />
          <p className="text-lg font-medium text-center px-2">
            Decisions thrive when rooted in data. Harness the power of insights
            to drive informed choices and unlock unparalleled success.
          </p>
          <div className="mt-4">
            <img src="/assets/logo.png" alt="" className="w-32" />
          </div>
        </section>
      </main>
    </div>
  );
};

export default Register;
